import { useState, useEffect, useCallback } from "react";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

// Ordered list of sidebar nav items to spotlight on a fresh admin's first sign-in.
const STEPS = [
  { testid: "nav-dashboard", title: "tour.dashboardT", body: "tour.dashboardB" },
  { testid: "nav-onboard", title: "tour.onboardT", body: "tour.onboardB" },
  { testid: "nav-employees", title: "tour.employeesT", body: "tour.employeesB" },
  { testid: "nav-actions", title: "tour.actionsT", body: "tour.actionsB" },
  { testid: "nav-settings", title: "tour.settingsT", body: "tour.settingsB" },
];

export function FirstLoginTour({ open, onClose }) {
  const { t } = useLang();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  // Only include steps whose target actually exists (perm-gated nav may be absent).
  const steps = STEPS.filter((s) => document.querySelector(`[data-testid="${s.testid}"]`));
  const step = steps[i];

  const measure = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(`[data-testid="${step.testid}"]`);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "nearest" });
    setRect(el.getBoundingClientRect());
  }, [step]);

  useEffect(() => {
    if (!open) return;
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, i, measure]);

  if (!open || !steps.length || !step) return null;

  const last = i === steps.length - 1;
  const finish = () => onClose && onClose();
  const pad = 6;
  const spotlightStyle = rect
    ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }
    : null;
  // Tooltip sits to the right of the sidebar item.
  const tipStyle = rect
    ? { top: Math.max(12, Math.min(rect.top - 8, window.innerHeight - 220)), left: rect.right + 20 }
    : { top: 80, left: 240 };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="first-login-tour">
      {/* click catcher — blocks the app while the tour is active */}
      <div className="absolute inset-0" onClick={() => {}} />
      {/* spotlight */}
      {spotlightStyle && (
        <div
          className="pointer-events-none absolute rounded-lg ring-2 ring-indigo-400 transition-all duration-300"
          style={{ ...spotlightStyle, boxShadow: "0 0 0 9999px rgba(9, 9, 20, 0.62)" }}
        />
      )}
      {/* tooltip card */}
      <div
        data-testid="tour-card"
        className="absolute w-[300px] rounded-xl border border-zinc-200 bg-white p-4 shadow-2xl transition-all duration-300"
        style={tipStyle}
      >
        <button onClick={finish} data-testid="tour-skip" className="absolute right-3 top-3 text-zinc-400 hover:text-zinc-700" aria-label={t("tour.skip")}>
          <X size={16} />
        </button>
        <div className="mb-1 font-display text-lg font-black uppercase tracking-tight text-zinc-950">{t(step.title)}</div>
        <p className="text-sm text-zinc-600">{t(step.body)}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="font-mono text-xs text-zinc-400">{t("tour.step").replace("{n}", i + 1).replace("{total}", steps.length)}</span>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setI(i - 1)} data-testid="tour-back">{t("tour.back")}</Button>
            )}
            {!last ? (
              <Button size="sm" onClick={() => setI(i + 1)} data-testid="tour-next">{t("tour.next")}</Button>
            ) : (
              <Button size="sm" onClick={finish} data-testid="tour-done">{t("tour.done")}</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
